// Identity and access contract shared by the API and the web app.

export const ROLES = ['MAHALLU_ADMIN', 'PRESIDENT', 'SECRETARY', 'TREASURER', 'COLLECTOR', 'WARD_REP', 'SADR', 'USTHAD', 'MEMBER'] as const;
export type Role = (typeof ROLES)[number];

/** Every role except an ordinary member must pass a second factor (owner decision, 4 Nov 2026). */
export function requiresSecondFactor(role: Role): boolean {
  return role !== 'MEMBER';
}

/** Roles that may read the whole house register. Ward and class scoping come later. */
export const REGISTER_ROLES: readonly Role[] = ['MAHALLU_ADMIN', 'PRESIDENT', 'SECRETARY', 'TREASURER'];

/** Roles that may add and edit houses and members. */
export const REGISTER_EDIT_ROLES: readonly Role[] = ['MAHALLU_ADMIN', 'SECRETARY'];

/** Roles that may change mahallu settings (numbering, profile, switches). */
export const SETTINGS_ROLES: readonly Role[] = ['MAHALLU_ADMIN', 'SECRETARY'];

/** Roles that see only the wards assigned to them. Each grant of these roles names one ward. */
export const WARD_SCOPED_ROLES: readonly Role[] = ['WARD_REP', 'COLLECTOR'];

/** Roles that may read the register: the whole of it (REGISTER_ROLES) or their own wards. */
export const REGISTER_READ_ROLES: readonly Role[] = [...REGISTER_ROLES, ...WARD_SCOPED_ROLES];

/** Roles that may give and take away access. Only a mahallu admin may change another admin. */
export const ACCESS_ROLES: readonly Role[] = ['MAHALLU_ADMIN', 'SECRETARY'];

export const OTP_CHANNELS = ['WHATSAPP', 'SMS', 'EMAIL'] as const;
export type OtpChannel = (typeof OTP_CHANNELS)[number];

export type MfaState = 'NOT_REQUIRED' | 'PENDING' | 'VERIFIED';

export interface MahalluChoice {
  slug: string;
  nameEn: string;
  nameMl: string | null;
  /** Primary role, used when a session starts in this mahallu. */
  role: Role;
  /** Every role the person holds here (ADR-0027). A session acts as one of them at a time. */
  roles: Role[];
}

export interface OtpStartResponse {
  challengeId: string;
  channel: OtpChannel;
  destination: string;
  expiresInSeconds: number;
  resendInSeconds: number;
}

export type SignInStatus = 'SIGNED_IN' | 'MFA_REQUIRED' | 'CHOOSE_MAHALLU';

export interface SignInStepResponse {
  status: SignInStatus;
  /** For MFA_REQUIRED: whether an authenticator is already set up. */
  enrolled?: boolean;
  /** For CHOOSE_MAHALLU. */
  mahallus?: MahalluChoice[];
  /** The person is an OptiFi console operator (ADR-0040). */
  console?: boolean;
}

export interface TotpSetupResponse {
  secret: string;
  otpauthUri: string;
  account: string;
}

export interface MeResponse {
  user: { displayName: string; destination: string; preferredLang: 'EN' | 'ML' };
  mahallu: { slug: string; nameEn: string; nameMl: string | null } | null;
  /** The active role of this session. */
  role: Role | null;
  /** Roles held in the current mahallu; switch with POST /api/auth/role. */
  roles: Role[];
  mfa: { state: MfaState; enrolled: boolean };
  mahallus: MahalluChoice[];
  /** This session is an OptiFi console session (ADR-0040). */
  console: boolean;
  /** The person's console role, if they are an OptiFi operator. */
  operator: 'OWNER' | 'SUPPORT' | null;
}

/** One role a person holds in a mahallu, optionally limited to one ward. */
export interface RoleGrant {
  id: string;
  role: Role;
  ward: { code: string; nameEn: string } | null;
}

/** A person with access to the mahallu, as the Users & roles screen shows them. */
export interface AccessUser {
  membershipId: string;
  displayName: string;
  /** Masked phone or email; the full value never leaves the server. */
  destination: string;
  status: 'ACTIVE' | 'REVOKED';
  primaryRole: Role;
  grants: RoleGrant[];
  mfaEnrolled: boolean;
  lastSeenAt: string | null;
  isYou: boolean;
}

export interface AccessInvite {
  /** Mobile number (Indian numbers without +91 are fine) or email. */
  identifier: string;
  displayName: string;
  role: Role;
  wardCode?: string | null;
}

export interface AccessGrant {
  role: Role;
  wardCode?: string | null;
}

/** One live sign-in of the current person, for Active sessions. */
export interface SessionRow {
  id: string;
  current: boolean;
  mahallu: string | null;
  role: Role | null;
  /** A short label from the browser, e.g. "Chrome on Android". */
  device: string;
  createdAt: string;
  lastSeenAt: string;
}

/** Machine-readable reasons the web app acts on (send to sign-in, ask for the second factor…). */
export type AuthErrorCode = 'SIGN_IN' | 'MFA_REQUIRED' | 'CHOOSE_MAHALLU' | 'FORBIDDEN' | 'CODE_WRONG' | 'CODE_EXPIRED' | 'RATE_LIMITED';
